import { useEffect, useRef, useState } from 'react';
import {
  setInstitutionalRuleLifecycle,
  type InstitutionalRuleRecord,
  type ReviewOrganization,
} from '@urbanik/api-client';
import { Button, CanonicalModal, TextAreaField } from '@urbanik/ui';
import { reviewApiBase } from './api-base';
export function InstitutionalRuleLifecycle({
  organization,
  rule,
  onClose,
  onSaved,
}: {
  organization: ReviewOrganization;
  rule: InstitutionalRuleRecord;
  onClose: () => void;
  onSaved: () => void;
}) {
  const annulled = !rule.deletedAt,
    [reason, setReason] = useState(''),
    [saving, setSaving] = useState(false),
    [error, setError] = useState('');
  const pending = useRef<AbortController | null>(null);
  useEffect(() => () => pending.current?.abort(), []);
  async function save() {
    if (saving || (annulled && reason.trim().length < 5)) return;
    const controller = new AbortController();
    pending.current = controller;
    setSaving(true);
    setError('');
    try {
      await setInstitutionalRuleLifecycle(
        reviewApiBase,
        organization.id,
        rule.id,
        rule.lineageId,
        rule.versionNumber,
        annulled,
        annulled ? reason.trim() : null,
        controller.signal,
      );
      if (!controller.signal.aborted) onSaved();
    } catch (e) {
      if (!controller.signal.aborted)
        setError(e instanceof Error ? e.message : 'No se pudo cambiar la regla.');
    } finally {
      if (!controller.signal.aborted) setSaving(false);
    }
  }
  return (
    <CanonicalModal
      open
      title={annulled ? 'Anular regla' : 'Restaurar regla'}
      description={organization.name + ' · ' + rule.code + ' · Versión ' + rule.versionNumber}
      onClose={() => {
        if (!saving) onClose();
      }}
    >
      {error ? <p role="alert">{error}</p> : null}
      <p>
        {annulled
          ? 'La regla dejará de estar vigente, pero todo su historial se conservará.'
          : 'La regla volverá a estar disponible sin borrar su historial.'}
      </p>
      {annulled ? (
        <TextAreaField
          label="Motivo de anulación"
          required
          minLength={5}
          maxLength={500}
          value={reason}
          disabled={saving}
          onChange={(e) => setReason(e.target.value)}
        />
      ) : null}
      <Button type="button" disabled={saving} onClick={onClose}>
        Cancelar
      </Button>
      <Button
        disabled={saving || (annulled && reason.trim().length < 5)}
        onClick={() => void save()}
      >
        {saving ? 'Procesando…' : annulled ? 'Confirmar anulación' : 'Confirmar restauración'}
      </Button>
    </CanonicalModal>
  );
}
